import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, FileText, Mail, TrendingUp, Clock, CheckCircle,
  AlertCircle, ChevronRight, Euro, Eye, Phone, MousePointer, Send,
  GraduationCap, AlertTriangle, Sparkles
} from 'lucide-react';
import api, { adminAPI, inscriptionsAPI, communicationsAPI, analyticsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import VisitsChart from './VisitsChart';

const greetingByHour = () => {
  const h = new Date().getHours();
  if (h < 6) return 'Bonne nuit';
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [analyticsStats, setAnalyticsStats] = useState(null);
  const [groupesStats, setGroupesStats] = useState(null);
  const [recentInscriptions, setRecentInscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, inscRes, analyticsRes, groupesRes] = await Promise.all([
        adminAPI.getStats(),
        inscriptionsAPI.list(),
        analyticsAPI.getStats().catch(() => ({ data: null })),
        api.get('/groupes/stats/overview').catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setRecentInscriptions(inscRes.data.slice(0, 5));
      setAnalyticsStats(analyticsRes.data);
      setGroupesStats(groupesRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get fill rate color
  const getFillRateColor = (taux) => {
    if (taux >= 90) return { bg: 'bg-red-500', text: 'text-red-600', bgLight: 'bg-red-100' };
    if (taux >= 70) return { bg: 'bg-yellow-500', text: 'text-yellow-600', bgLight: 'bg-yellow-100' };
    return { bg: 'bg-green-500', text: 'text-green-600', bgLight: 'bg-green-100' };
  };

  const statCards = [
    { name: 'Inscriptions totales', value: stats.total_inscriptions || 0, icon: FileText, color: 'bg-blue-500' },
    { name: 'En attente', value: stats.pending_inscriptions || 0, icon: Clock, color: 'bg-yellow-500' },
    { name: 'Validées', value: stats.validated_inscriptions || 0, icon: CheckCircle, color: 'bg-green-500' },
    { name: 'Messages non lus', value: stats.unread_messages || 0, icon: Mail, color: 'bg-red-500' },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      validated: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    const labels = { pending: 'En attente', validated: 'Validée', paid: 'Payée', cancelled: 'Annulée' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Default password warning */}
        {(user?.role === 'super_admin' || user?.role === 'admin') && !user?.password_changed_at && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-rose-50 p-4 flex items-center gap-3"
            data-testid="default-password-banner"
          >
            <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900">Sécurité : changez votre mot de passe par défaut</p>
              <p className="text-sm text-amber-800">
                Vous utilisez encore un mot de passe par défaut. Avant la mise en production, choisissez un mot de passe fort et unique depuis la sidebar (« Mot de passe »).
              </p>
            </div>
          </motion.div>
        )}

        {/* Personalized welcome banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#285BBB] via-[#2C3C65] to-[#285BBB] text-white p-6"
          data-testid="welcome-banner"
        >
          <div className="absolute -right-10 -top-10 w-48 h-48 bg-white/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-amber-300/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 bg-white/15 backdrop-blur rounded-2xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-7 w-7 text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white/70 mb-0.5">{greetingByHour()},</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                Hello {user?.prenom || 'là'} !{' '}
                <span className="inline-block animate-wave">👋</span>
              </h1>
              <p className="text-sm text-white/80 mt-1">
                {user?.role === 'super_admin' && "Voici les dernières activités de l'école."}
                {user?.role === 'admin' && "Voici les dernières activités de l'école."}
                {user?.role === 'agence' && "Voici les dernières activités du compte agence."}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-[#2C3C65]">Tableau de bord</h2>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl shadow-md p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#6777A1]">{stat.name}</p>
                    <p className="text-3xl font-bold text-[#2C3C65] mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Revenue / VAT Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-md p-6"
          data-testid="revenue-card"
        >
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-bold text-[#2C3C65]">Chiffre d'affaires</h2>
              <p className="text-sm text-[#6777A1]">
                Inscriptions validées + payées · TVA {stats.vat_rate ?? 20}%
                {stats.prices_are_ttc !== undefined && (
                  <span className="ml-2 text-xs px-2 py-0.5 bg-slate-100 rounded-full">
                    Saisie {stats.prices_are_ttc ? 'TTC' : 'HT'}
                  </span>
                )}
              </p>
            </div>
            <Link to="/admin/inscriptions" className="text-sm text-[#285BBB] hover:underline font-medium">
              Détail des inscriptions →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl p-5 bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-200">
              <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-1">CA HT</p>
              <p className="text-3xl font-bold text-emerald-900">{(stats.ca_ht || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
              <p className="text-xs text-emerald-700/70 mt-1">Hors taxes</p>
            </div>
            <div className="rounded-xl p-5 bg-gradient-to-br from-amber-50 to-amber-100/40 border border-amber-200">
              <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-1">TVA collectée</p>
              <p className="text-3xl font-bold text-amber-900">{(stats.ca_tva || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
              <p className="text-xs text-amber-700/70 mt-1">À reverser à l'État</p>
            </div>
            <div className="rounded-xl p-5 bg-gradient-to-br from-blue-50 to-blue-100/40 border border-blue-200">
              <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold mb-1">CA TTC</p>
              <p className="text-3xl font-bold text-blue-900">{(stats.ca_ttc || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
              <p className="text-xs text-blue-700/70 mt-1">Toutes taxes comprises</p>
            </div>
          </div>
          {stats.paid_ttc > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs text-[#6777A1]">Encaissé HT</p>
                <p className="font-semibold text-emerald-700">{(stats.paid_ht || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
              </div>
              <div>
                <p className="text-xs text-[#6777A1]">Encaissé TVA</p>
                <p className="font-semibold text-amber-700">{(stats.paid_tva || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
              </div>
              <div>
                <p className="text-xs text-[#6777A1]">Encaissé TTC</p>
                <p className="font-semibold text-blue-700">{(stats.paid_ttc || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Analytics Section */}
        {analyticsStats && (
          <motion.div            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] rounded-xl shadow-md p-6 text-white"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Statistiques du site (ce mois)
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Visites */}
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="h-4 w-4 text-blue-200" />
                  <span className="text-blue-200 text-sm">Visites</span>
                </div>
                <p className="text-2xl font-bold">{analyticsStats.visits?.month || 0}</p>
                <p className="text-xs text-blue-200">Aujourd'hui: {analyticsStats.visits?.today || 0}</p>
              </div>
              
              {/* Clics Téléphone */}
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="h-4 w-4 text-green-300" />
                  <span className="text-green-200 text-sm">Clics Tél</span>
                </div>
                <p className="text-2xl font-bold">{analyticsStats.clicks?.month?.phone || 0}</p>
                <p className="text-xs text-green-200">Aujourd'hui: {analyticsStats.clicks?.today?.phone || 0}</p>
              </div>
              
              {/* Clics Email */}
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-yellow-300" />
                  <span className="text-yellow-200 text-sm">Clics Email</span>
                </div>
                <p className="text-2xl font-bold">{analyticsStats.clicks?.month?.email || 0}</p>
                <p className="text-xs text-yellow-200">Aujourd'hui: {analyticsStats.clicks?.today?.email || 0}</p>
              </div>
              
              {/* Demandes de devis */}
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Send className="h-4 w-4 text-red-300" />
                  <span className="text-red-200 text-sm">Devis</span>
                </div>
                <p className="text-2xl font-bold">{analyticsStats.devis?.total || 0}</p>
                <p className="text-xs text-red-200">Nouveaux: {analyticsStats.devis?.new || 0}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Visits Chart - evolution over last 30 days */}
        <VisitsChart />

        {/* Groupes Stats Section */}
        {groupesStats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-[#2C3C65] flex items-center">
                <GraduationCap className="h-5 w-5 mr-2 text-[#285BBB]" />
                Taux de remplissage des groupes
              </h2>
              <Link to="/admin/groupes" className="text-[#285BBB] hover:underline flex items-center text-sm">
                Gérer les groupes
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-[#F5F7FF] rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-[#2C3C65]">{groupesStats.total_groupes}</p>
                <p className="text-xs text-[#6777A1]">Groupes</p>
              </div>
              <div className="bg-[#F5F7FF] rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-[#2C3C65]">{groupesStats.total_eleves}</p>
                <p className="text-xs text-[#6777A1]">Élèves</p>
              </div>
              <div className="bg-[#F5F7FF] rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-[#2C3C65]">{groupesStats.taux_moyen}%</p>
                <p className="text-xs text-[#6777A1]">Taux moyen</p>
              </div>
              <div className={`rounded-lg p-4 text-center ${groupesStats.groupes_critiques?.length > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <p className={`text-2xl font-bold ${groupesStats.groupes_critiques?.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {groupesStats.groupes_critiques?.length || 0}
                </p>
                <p className="text-xs text-gray-600">Groupes pleins</p>
              </div>
            </div>

            {/* Alerts for critical groups */}
            {groupesStats.groupes_critiques?.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium text-red-700">Groupes presque complets (≥90%)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {groupesStats.groupes_critiques.map((g) => (
                    <span key={g.id} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                      {g.nom} ({g.taux_remplissage}%)
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Top 5 Groups by fill rate */}
            <div className="space-y-3">
              {groupesStats.groupes?.slice(0, 5).map((groupe) => {
                const colors = getFillRateColor(groupe.taux_remplissage);
                return (
                  <div key={groupe.id} className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-[#2C3C65]">{groupe.nom}</span>
                        <span className={`text-xs font-semibold ${colors.text}`}>
                          {groupe.eleves_count}/{groupe.capacite_max} ({groupe.taux_remplissage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`${colors.bg} h-2 rounded-full transition-all duration-300`}
                          style={{ width: `${Math.min(groupe.taux_remplissage, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Recent Inscriptions */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-[#2C3C65]">Dernières inscriptions</h2>
            <Link to="/admin/inscriptions" className="text-[#285BBB] hover:underline flex items-center">
              Voir tout
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
            </div>
          ) : recentInscriptions.length === 0 ? (
            <p className="text-center text-[#6777A1] py-8">Aucune inscription</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6777A1]">N°</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6777A1]">Enfant(s)</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6777A1]">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6777A1]">Total</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6777A1]">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInscriptions.map((inscription) => (
                    <tr key={inscription.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <Link to={`/admin/inscriptions/${inscription.id}`} className="text-[#285BBB] font-medium hover:underline">
                          {inscription.numero}
                        </Link>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {inscription.enfants?.map(e => `${e.prenom} ${e.nom}`).join(', ')}
                      </td>
                      <td className="py-3 px-4 text-sm text-[#6777A1]">
                        {new Date(inscription.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="py-3 px-4 font-medium">{inscription.total}€</td>
                      <td className="py-3 px-4">{getStatusBadge(inscription.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6">
          <Link
            to="/admin/inscriptions"
            className="bg-[#285BBB] text-white rounded-xl p-6 hover:bg-[#2C3C65] transition-colors"
          >
            <FileText className="h-8 w-8 mb-3" />
            <h3 className="font-semibold">Gérer les inscriptions</h3>
            <p className="text-sm text-blue-200 mt-1">Valider, modifier, annuler</p>
          </Link>
          <Link
            to="/admin/communications"
            className="bg-[#ff3a2d] text-white rounded-xl p-6 hover:bg-[#e6352a] transition-colors"
          >
            <AlertCircle className="h-8 w-8 mb-3" />
            <h3 className="font-semibold">Nouvelle communication</h3>
            <p className="text-sm text-red-200 mt-1">Informer les parents</p>
          </Link>
          <Link
            to="/admin/settings"
            className="bg-gray-700 text-white rounded-xl p-6 hover:bg-gray-800 transition-colors"
          >
            <TrendingUp className="h-8 w-8 mb-3" />
            <h3 className="font-semibold">Ouvrir/Fermer inscriptions</h3>
            <p className="text-sm text-gray-300 mt-1">Gérer la période</p>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
